import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {notifyLoadFlow, notifyPositionLeft} from '@/helpers/loaderAPI';
import {setFlow} from '@/store/slices/chatbotFlow';
import {
    showChatbotWithDelay,
    showPopupMessageWithDelay,
} from '@/store/slices/chatbotVisibility';
import {apiClient} from '@/helpers/axios';
import {canAutoOpenChatbot, canShowPopupMessage} from '@/helpers/popupStorage';

const dispatchChatbotDetails = (thunkAPI, chatbot) => {
    thunkAPI.dispatch(
        setDetails({
            settings: chatbot.settings,
            company: chatbot.company,
            logo: chatbot.logo,
        }),
    );
};

const shouldAutoOpenChatbot = (settings, isEverOpened) => {
    if (!settings?.autoPopup || isEverOpened) return false;
    return canAutoOpenChatbot();
};

// Mobile Experience setting, only an explicit false turns it off
const isMobileExperienceDisabled = (settings) => settings?.mobileExperience === false;

const shouldShowPopupMessage = (settings, isEverOpened) => {
    if (!settings?.popupMessageEnabled || isEverOpened) return false;
    if (isMobileExperienceDisabled(settings)) return true;
    return canShowPopupMessage();
};

const handleChatbotVisibility = (thunkAPI, chatbot, isDirectLink, isEverOpened) => {
    const settings = chatbot?.settings;
    if (isDirectLink) {
        thunkAPI.dispatch(showChatbotWithDelay(5));
        return;
    }
    if (shouldAutoOpenChatbot(settings, isEverOpened)) {
        setTimeout(() => {
            thunkAPI.dispatch(showChatbotWithDelay(5));
        }, settings?.secondsUntilPopup * 1000);
        return;
    }
    if (shouldShowPopupMessage(settings, isEverOpened)) {
        thunkAPI.dispatch(
            showPopupMessageWithDelay(settings?.secondsUntilPopupMessage * 1000),
        );
    }
};

const chatbotHasBlocks = (chatbot) => {
    return chatbot?.flow?.groups?.some(group => group?.blocks?.length > 0);
};

const handleInactiveChatbot = (thunkAPI, chatbot, isDirectLink) => {
    thunkAPI.dispatch(setIsInactive(true));
    dispatchChatbotDetails(thunkAPI, chatbot);
    if (isDirectLink) {
        thunkAPI.dispatch(showChatbotWithDelay(5));
    }
};

const handleEmptyBlocksChatbot = (thunkAPI, chatbot, isDirectLink) => {
    thunkAPI.dispatch(setHasNoBlocks(true));
    dispatchChatbotDetails(thunkAPI, chatbot);
    if (isDirectLink) {
        thunkAPI.dispatch(showChatbotWithDelay(5));
    }
};

const handleActiveChatbot = (thunkAPI, chatbot, isDirectLink, isEverOpened) => {
    dispatchChatbotDetails(thunkAPI, chatbot);
    thunkAPI.dispatch(setFlow(chatbot?.flow));
    thunkAPI.dispatch(setLoading(false));
    if (chatbot?.settings?.position === 'left') notifyPositionLeft();
    notifyLoadFlow();
    handleChatbotVisibility(thunkAPI, chatbot, isDirectLink, isEverOpened);
};

export const loadChatbot = createAsyncThunk(
    'chatbotDetails/loadChatbot',
    async (_, thunkAPI) => {
        const {UUID, isDirectLink} = thunkAPI.getState().chatbotDetails;
        const {isEverOpened} = thunkAPI.getState().chatbotVisibility;
        thunkAPI.dispatch(setLoading(true));
        apiClient.get(`/assistants/${UUID}/detail/`)
            .then(function (response) {
                if (response.status !== 200) return;
                const chatbot = response.data;
                if (!chatbot.active) {
                    handleInactiveChatbot(thunkAPI, chatbot, isDirectLink);
                    return;
                }
                if (!chatbotHasBlocks(chatbot)) {
                    handleEmptyBlocksChatbot(thunkAPI, chatbot, isDirectLink);
                    return;
                }
                handleActiveChatbot(thunkAPI, chatbot, isDirectLink, isEverOpened);
            })
            .catch(function (error) {
                console.info(`Couldn\'t load the chatbot with id: ${UUID}. Id could be invalid`, error);
            })
            .finally(() => {
                thunkAPI.dispatch(setLoading(false));
            });
    },
);


const chatbotDetails = createSlice({
    name: 'chatbotDetails',
    initialState: {
        loading: false,
        candidateID: null,
        sourceID: null,
        exid: null,
        isDirectLink: false,
        UUID: '',
        settings: {},
        company: {},
        logo: '',
        isInactive: false,
        hasNoBlocks: false,
    },
    reducers: {
        setUUID: (state, action) => {
            state.UUID = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setDetails: (state, action) => {
            const {
                settings = {},
                company = {},
                logo = '',
            } = action.payload;
            state.settings = settings;
            state.company = company;
            state.logo = logo;
        },
        setCandidateID: (state, action) => {
            state.candidateID = action.payload;
        },
        setSourceID: (state, action) => {
            state.sourceID = action.payload;
        },
        setExid: (state, action) => {
            state.exid = action.payload;
        },
        setIsDirectLink: (state, action) => {
            state.isDirectLink = action.payload;
        },
        setIsInactive: (state, action) => {
            state.isInactive = action.payload;
        },
        setHasNoBlocks: (state, action) => {
            state.hasNoBlocks = action.payload;
        },
    },
});

export const {
    setUUID,
    setDetails,
    setLoading,
    setCandidateID,
    setSourceID,
    setExid,
    setIsDirectLink,
    setIsInactive,
    setHasNoBlocks,
} = chatbotDetails.actions;

export default chatbotDetails.reducer;
